import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, X, Hash } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IdFornitori = () => {
  const [idFornitori, setIdFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ codice: '', descrizione: '' });

  useEffect(() => {
    fetchIdFornitori();
  }, []);

  const fetchIdFornitori = async () => {
    try {
      const response = await axios.get(`${API}/id-fornitori`);
      setIdFornitori(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli ID');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/id-fornitori`, formData);
      toast.success('ID creato con successo');
      fetchIdFornitori();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (codice) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'ID "${codice}"?`)) return;
    try {
      await axios.delete(`${API}/id-fornitori/${encodeURIComponent(codice)}`);
      toast.success('ID eliminato con successo');
      fetchIdFornitori();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({ codice: '', descrizione: '' });
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="id-fornitori-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-cyan-500 p-2 rounded-xl">
            <Hash className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Archivio ID</h2>
            <p className="text-xs text-white/50">ID per identificare i fornitori reali che portano la merce</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
            showForm 
              ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20' 
              : 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20 hover:bg-cyan-600'
          }`}
          data-testid="add-id-btn"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Annulla' : 'Nuovo ID'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="id-form">
          <h3 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Nuovo ID Fornitore</h3>
          <form onSubmit={handleSubmit} className="flex gap-4 items-end flex-wrap">
            <div className="w-32">
              <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Codice/Sigla</label>
              <input
                type="text"
                value={formData.codice}
                onChange={(e) => setFormData({ ...formData, codice: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono font-bold"
                required
                placeholder="es. 1, A1"
                data-testid="id-codice-input"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Descrizione (opzionale)</label>
              <input
                type="text"
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="es. Mario Rossi - Pescatore"
                data-testid="id-descrizione-input"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-cyan-500/20 hover:bg-cyan-600 transition-colors"
              data-testid="save-id-btn"
            >
              <Save className="w-4 h-4" />
              Salva
            </button>
          </form>
        </div>
      )}

      {/* Tabella */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="id-table-container">
        <table className="text-sm w-full border-collapse" data-testid="id-table">
          <thead>
            <tr className="bg-cyan-500/10">
              <th className="text-cyan-400 uppercase text-xs font-bold py-3 px-4 text-center w-24">ID</th>
              <th className="text-cyan-400 uppercase text-xs font-bold py-3 px-4 text-left">Descrizione</th>
              <th className="text-cyan-400 uppercase text-xs font-bold py-3 px-4 text-left w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {idFornitori.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center py-8 text-white/50" data-testid="no-id-message">
                  Nessun ID presente. Crea il primo ID per identificare i fornitori reali.
                </td>
              </tr>
            ) : (
              idFornitori.map((id, index) => (
                <tr key={id.codice} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`id-row-${index}`}>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-lg font-mono font-bold text-lg">
                      {id.codice}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white/70">{id.descrizione || '-'}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleDelete(id.codice)}
                      className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      data-testid={`delete-id-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IdFornitori;
